import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import * as Types from './type'
import { AppDispatch } from '@/store'
import Api from '@/services/http'
import { getFeatureKeys, getServiceKeys, getUserPermissionTree } from '@/services/api'
import Cookies from 'js-cookie'
import { getLanguageList } from '@/hooks/useLanguage'
import { thunkDebounce, waitStore } from '../storeUtils'

const initialState: Types.IAuthState = {
  isLogined: !!(Cookies.get('osudb_uid') && Cookies.get('osudb_appid')),
  osudb_uid: Cookies.get('osudb_uid') || '',
  osudb_subappid: Cookies.get('osudb_subappid') || '',
  authResource: null,
  userInfo: null,
  userSettingMap: {},
  featureKeys: [],
  serviceKeys: [],
  languageList: [],
  walletList: [],
  permissionTree: null,
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * 设置登录信息
     * @param state
     * @param action
     * @returns
     */
    setLogin(state, action: PayloadAction<Types.IUserState>) {
      return {
        ...state,
        isLogined: action.payload.isLogined,
        osudb_uid: action.payload.osudb_uid,
        osudb_subappid: action.payload.osudb_subappid,
      }
    },
    /**
     * 设置权限树
     * @param state
     * @param action
     * @returns
     */
    setAuthResource(state, action: PayloadAction<Types.IAuthState['authResource']>) {
      return {
        ...state,
        authResource: action.payload,
      }
    },
    /**
     * 设置商户信息
     * @param state
     * @param action
     * @returns
     */
    setUserInfo(state, action: PayloadAction<Types.IAuthState['userInfo']>) {
      return {
        ...state,
        userInfo: action.payload,
      }
    },
    setWalletListInfo(state, action: PayloadAction<Types.IWallInfo[]>) {
      return {
        ...state,
        walletList: action.payload,
      }
    },
    // 更新钱包信息
    updateWalletListInfo(state, action: PayloadAction<Types.IWallInfo>) {
      const newWalletInfo = action.payload
      const walletList = [...state.walletList]
      const updateListInd = walletList.findIndex((item) => item.currencyCode === newWalletInfo.currencyCode)
      walletList[updateListInd] = newWalletInfo

      return {
        ...state,
        walletList,
      }
    },

    setSelectInvalidCountry(state, action: PayloadAction<string>) {
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          selectInvalidCountry: action.payload,
        },
      }
    },
    setUserSettingList(state, action: PayloadAction<Types.ISettingMap>) {
      return {
        ...state,
        userSettingMap: {
          ...state.userSettingMap,
          ...action.payload,
        },
      }
    },
    setFeatureKeys(state, action: PayloadAction<any>) {
      return {
        ...state,
        featureKeys: action.payload,
      }
    },
    setServiceKeys(state, action: PayloadAction<any>) {
      return {
        ...state,
        serviceKeys: action.payload,
      }
    },
    setLanguageList(state, action: PayloadAction<Types.IAuthState['languageList']>) {
      return {
        ...state,
        languageList: action.payload,
      }
    },
    setPermissionTree(state, action: PayloadAction<Types.IAuthState['permissionTree']>) {
      return {
        ...state,
        permissionTree: action.payload,
      }
    },
  },
})

// 更新接收营销政策
export const updateAcceptMarketing = async (acceptMarketing = true) => {
  const resp = await Api.admin.post('/oneclub-api/api/user/info/get-basic')
  const oneshipName = resp?.data?.oneshipName
  return Api.admin.post('/oneclub-api/api/user/info/update-basic', { oneshipName, acceptMarketing })
}

export const checkLogin = () => async (dispatch: AppDispatch) => {
  const osudb_uid = Cookies.get('osudb_uid') || ''
  const osudb_appid = Cookies.get('osudb_appid') || ''
  const osudb_subappid = Cookies.get('osudb_subappid') || ''
  const isLogined = !!(osudb_uid && osudb_appid)
  const newUserState: Types.IUserState = {
    isLogined,
    osudb_uid,
    osudb_subappid,
  }
  dispatch(authSlice.actions.setLogin(newUserState))
  return newUserState
}

export const loginout = () => async (dispatch: AppDispatch) => {
  const rst = await Api.udb.get('/udb/lgn/login/logout.do', {
    params: {
      subappid: process.env.SUB_APPID,
      appid: process.env.APPID,
    },
  })
  const { rescode } = rst
  if (rescode === '0') {
    Cookies.remove('osudb_uid')
    Cookies.remove('osudb_appid')
    Cookies.remove('osudb_subappid')
    Cookies.remove('authorization')
    // 清除
    window.localStorage.removeItem('os-init-user-default-lang')
    window.sessionStorage.clear()
    // 多版本的cookie
    Cookies.remove('AQM_VERSION')
    dispatch(authSlice.actions.setLogin({ isLogined: false, osudb_uid: '', osudb_subappid: '' }))
  }
}

/** 上报设置切换国家 */
export const postSetCountry = (params: Types.SetCountry) => async () => {
  const res = await Api.admin.post<Types.SetCountry>('/api/user/info/country/change', params)
  const { data, code } = res
  if (code === 'SUCCESS') {
    window.localStorage.setItem('lastArea', params.sourceCountry || '')
    return data
  }
  return Promise.reject(res)
}

/** 获取用户fk */
export const fetchFeatureKeys = thunkDebounce(() => async (dispatch: AppDispatch) => {
  const response = await getFeatureKeys()
  const { data, code, bizMessage, message } = response
  if (code === 'SUCCESS') {
    dispatch(authSlice.actions.setFeatureKeys(data.featureKeys))
    return
  }

  return Promise.reject({ code, bizMessage, message })
})

/** 商家有效service key查询 */
export const fetchServiceKeys = (params?: Parameters<typeof getServiceKeys>[0]) => async (dispatch: AppDispatch) => {
  const response = await getServiceKeys(params)
  const { data, code, bizMessage, message } = response
  if (code === 'SUCCESS') {
    dispatch(authSlice.actions.setServiceKeys(data.serviceRights))
    return data.serviceRights
  }

  return Promise.reject({ code, bizMessage, message })
}

/**
 * 是否更新钱包信息
 * @param shouldUpdateWallet
 * @returns
 */
export const getUserInfo =
  (shouldUpdateWallet = false) =>
  async (dispatch: AppDispatch) => {
    const { code, message, data: userInfo } = await Api.admin.post<Types.UserInfo>('/api/user/info/get')
    if (code === 'SUCCESS') {
      /** getLanguageList依赖配置 */
      await waitStore((s) => s.config.ready)

      const { countryCode, settlementMethod, displayWallet, countryCurrencyCode } = userInfo
      const lastArea = countryCode || ''
      window.localStorage.setItem('lastArea', lastArea)
      const isPrepaid = settlementMethod === 'prepaid'
      dispatch(
        authSlice.actions.setUserInfo({
          ...userInfo,
          selfArea: lastArea,
          isPrepaid,
          displayWallet,
          currencyCode: countryCurrencyCode,
        })
      )
      dispatch(setLanguageList(getLanguageList(countryCode)))
      if (shouldUpdateWallet && displayWallet) {
        dispatch(getWalletInfo())
      }
      return userInfo
    }

    return Promise.reject(message)
  }

export const getWalletInfo = () => async (dispatch: AppDispatch) => {
  const { data } = await Api.normal.post<{ list: Types.IWallInfo[] }>('/api/wallet/admin/info/list')
  const walletList = data?.list || []
  dispatch(authSlice.actions.setWalletListInfo(walletList))
  return walletList
}

/**
 * 初始化权限树
 * @returns
 */
export const initUserPermissionTree = () => async (dispatch: AppDispatch) => {
  const res = await getUserPermissionTree()
  dispatch(setPermissionTree(res.data.resourceTree))
}

export const { setAuthResource, setLogin, setUserSettingList, setUserInfo, setWalletListInfo, updateWalletListInfo, setLanguageList, setPermissionTree } = authSlice.actions

export default authSlice.reducer
